import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { PlayfulToast } from './PlayfulToast';
import { PlayfulMessage, MessageCategory, getRandomMessage } from './playfulMessages';
import { playNotificationChime } from '../../utils/soundEffects';

// Settings interface
interface PlayfulToastSettings {
    enabled: boolean;
    intervalMinutes: number;
    categories: MessageCategory[];
    soundEnabled: boolean;
}

// Default settings
const DEFAULT_SETTINGS: PlayfulToastSettings = {
    enabled: true,
    intervalMinutes: 30,
    categories: ['sales_wisdom', 'coffee_break', 'motivation', 'humor', 'wellness'],
    soundEnabled: false, // Default to off to not annoy users
};

// Local storage key
const STORAGE_KEY = 'playful_toast_settings';

// Context interface
interface PlayfulToastContextValue {
    settings: PlayfulToastSettings;
    updateSettings: (newSettings: Partial<PlayfulToastSettings>) => void;
    showMessage: (message?: PlayfulMessage) => void;
    hideMessage: () => void;
    triggerRandomMessage: () => void;
}

const PlayfulToastContext = createContext<PlayfulToastContextValue | null>(null);

// Hook to use playful toast
export const usePlayfulToast = () => {
    const context = useContext(PlayfulToastContext);
    if (!context) {
        throw new Error('usePlayfulToast must be used within a PlayfulToastProvider');
    }
    return context;
};

// Provider component
export const PlayfulToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Load settings from localStorage
    const loadSettings = (): PlayfulToastSettings => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
            }
        } catch (e) {
            console.error('Failed to load playful toast settings:', e);
        }
        return DEFAULT_SETTINGS;
    };

    const [settings, setSettings] = useState<PlayfulToastSettings>(loadSettings);
    const [currentMessage, setCurrentMessage] = useState<PlayfulMessage | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const lastShownRef = useRef<number>(Date.now());

    // Save settings to localStorage
    const updateSettings = useCallback((newSettings: Partial<PlayfulToastSettings>) => {
        setSettings(prev => {
            const updated = { ...prev, ...newSettings };
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
            } catch (e) {
                console.error('Failed to save playful toast settings:', e);
            }
            return updated;
        });
    }, []);

    // Show a specific or random message
    const showMessage = useCallback((message?: PlayfulMessage) => {
        const messageToShow = message || getRandomMessage(settings.categories);
        setCurrentMessage(messageToShow);
        setIsVisible(true);
        lastShownRef.current = Date.now();

        // Play sound if enabled
        if (settings.soundEnabled) {
            playNotificationChime(0.2);
        }
    }, [settings.categories, settings.soundEnabled]);

    // Hide current message
    const hideMessage = useCallback(() => {
        setIsVisible(false);
        // Small delay before clearing to allow exit animation
        setTimeout(() => setCurrentMessage(null), 500);
    }, []);

    // Trigger a random message
    const triggerRandomMessage = useCallback(() => {
        if (!settings.enabled) return;

        const message = getRandomMessage(settings.categories);
        showMessage(message);
    }, [settings.enabled, settings.categories, showMessage]);

    // Timer logic
    useEffect(() => {
        if (!settings.enabled) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        const intervalMs = settings.intervalMinutes * 60 * 1000;

        // Check if tab is visible before showing
        const checkAndShow = () => {
            if (document.visibilityState === 'visible') {
                const timeSinceLastShow = Date.now() - lastShownRef.current;
                if (timeSinceLastShow >= intervalMs) {
                    triggerRandomMessage();
                }
            }
        };

        // Set up interval
        intervalRef.current = setInterval(checkAndShow, 60000); // Check every minute

        // Also check on visibility change
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkAndShow();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [settings.enabled, settings.intervalMinutes, triggerRandomMessage]);

    // Expose for debugging/manual trigger
    useEffect(() => {
        (window as any).__playfulToast = {
            show: triggerRandomMessage,
            settings,
            updateSettings,
        };
        return () => {
            delete (window as any).__playfulToast;
        };
    }, [triggerRandomMessage, settings, updateSettings]);

    return (
        <PlayfulToastContext.Provider
            value={{
                settings,
                updateSettings,
                showMessage,
                hideMessage,
                triggerRandomMessage,
            }}
        >
            {children}
            {currentMessage && (
                <PlayfulToast
                    message={currentMessage}
                    isVisible={isVisible}
                    onClose={hideMessage}
                    duration={8000}
                />
            )}
        </PlayfulToastContext.Provider>
    );
};

export default PlayfulToastProvider;
